import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Loader, ErrorFallback } from 'smart-react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import AdminLookupDetails from '../../LookupDetails/Pages/LookupDetails';
import { isAdminRole } from '../../../../Utils/Auth/ValidatePermissions';
import {
  base64ToObject,
  objectToBase64,
} from '../../../../Utils/Common/commonUtils';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import { useLocation, useNavigate } from 'react-router-dom';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../Core/Context/AuthContext';
import NoPermission from '../../../Core/NoPermission/NoPermission';

/**
 * Add New Lookup main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Lookup component.
 */
const AddLookup = () => {
  const { user } = useAuth(); // Get tenantID using Auth Context
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const [noRecord, setNoRecord] = React.useState(false);
  const { getParam } = useQueryParams();
  const refId = getParam('refId');

  const { state } = useLocation();

  const [tempState, setTempState] = React.useState(state);

  /**
   * Set Lookup Data
   * @param {*} stateData
   */
  const setLookupData = async (stateData) => {
    try {
      let refObj = base64ToObject(refId);
      if (!refObj?.Type) {
        setNoRecord(true);
      } else {
        setNoRecord(false);
        setTempState({ ...stateData, dataSet: { ...refObj }, edit: true });
      }
    } catch (error) {
      // Handle error state if needed
      setNoRecord(true);
    }
  };

  React.useEffect(() => {
    if (tempState || (refId && refId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      setLookupData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setTempState({ ...state });
      }
    }
  }, [state]);

  /**
   * onExit
   * back to lookup listing screen
   */
  const onExit = () => {
    navigate('/admin/lookups');
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        {!isAdminRole(user) && <NoPermission />}
        {isAdminRole(user) && (
          <React.Suspense fallback={<Loader />}>
            {loading && <Loader />}
            {noRecord ? (
              <NoRecordFound
                recordNotFoundText={'No Record Found'}
                onExit={onExit}
              />
            ) : (
              <>
                <div
                  className={`add-rf-profile-group main-page-section add-page-section `}
                >
                  <Card className='tophead'>
                    <CardHeader>
                      <div className='action-buttons-container'>
                        <Link
                          className='action-icon-wrapper !k-d-block'
                          to={`/admin/lookups`}
                        >
                          <FontAwesomeIcon
                            className='k-mr-2 k-pt-3'
                            icon={faArrowLeft}
                          />
                        </Link>
                        <div>
                          <p className='display-text k-mb-0'>
                            Lookup: {tempState?.dataSet?.Type}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  <Card className='k-mt-3 detail-grid-section'>
                    <CardBody>
                      <AdminLookupDetails lookup={tempState} />
                    </CardBody>
                  </Card>
                </div>
              </>
            )}
          </React.Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};
export default AddLookup;
